import type { User } from 'oidc-client-ts';

import type { AuthentificatedUser } from 'src/types/domain/AuthentificatedUser';
import type { SsoTokenClaims } from 'src/types/domain/SsoTokenClaims';

class AuthService {
  formatSsoUser(user: User): AuthentificatedUser | null {
    if (!user) return null;
    const tokenClaims = this.parseJwtToken(user.access_token);
    const userId = user.profile.sub || user.profile.id || tokenClaims.sub;
    if (!userId) return null;
    return {
      id: userId as string,
      username: user.profile.preferred_username || user.profile.name || '',
      email: user.profile.email,
      firstName: user.profile.given_name,
      lastName: user.profile.family_name,
      roles: this.getAllRoles(tokenClaims),
      realmRoles: tokenClaims.realm_access?.roles || [],
      clientRoles: this.extractClientRoles(tokenClaims),
      resourceAccess: tokenClaims.resource_access || {},
      groups: tokenClaims.groups,
      emailVerified: user.profile.email_verified,
      enabled: true,
    };
  }

  parseJwtToken(token: string): SsoTokenClaims {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch {
      return { sub: '' } as SsoTokenClaims;
    }
  }

  getAllRoles(claims: SsoTokenClaims): string[] {
    const roles: string[] = [];

    if (claims.realm_access?.roles) {
      roles.push(...claims.realm_access.roles);
    }

    if (claims.resource_access) {
      Object.values(claims.resource_access).forEach((access) => {
        if (access.roles) {
          roles.push(...access.roles);
        }
      });
    }

    return [...new Set(roles)];
  }

  extractClientRoles(claims: SsoTokenClaims): Record<string, string[]> {
    const clientRoles: Record<string, string[]> = {};

    if (claims.resource_access) {
      Object.entries(claims.resource_access).forEach(([clientId, access]) => {
        if (access.roles) {
          clientRoles[clientId] = access.roles;
        }
      });
    }

    return clientRoles;
  }
}

export const authService = new AuthService();
